import React from "react";
import * as Sentry from "@sentry/react";
import ErrorSplash from "./ErrorSplash";
import {
  isChunkLoadError,
  retryCurrentDocument,
  CHUNK_RETRY_PARAM,
} from "./chunkLoadRecovery";

const FILTER_STRING = "ResizeObserver";

const isChunkRetryInProgress = () =>
  new URLSearchParams(window.location.search).has(CHUNK_RETRY_PARAM);

interface ErrorMessageParams {
  error: unknown;
}

const getErrorMessage = ({ error }: ErrorMessageParams) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    const message = (error as { message?: unknown }).message;

    return typeof message === "string" ? message : "";
  }

  return "";
};

const shouldIgnoreError = ({ error }: ErrorMessageParams) =>
  getErrorMessage({ error }).includes(FILTER_STRING);

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: unknown;
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    error: undefined,
    hasError: false,
  };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState | null {
    if (shouldIgnoreError({ error })) {
      return null;
    }

    // Suppress error UI on first chunk load failure — let componentDidCatch retry
    if (isChunkLoadError({ error }) && !isChunkRetryInProgress()) {
      return null;
    }

    return {
      error,
      hasError: true,
    };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    if (shouldIgnoreError({ error })) {
      return;
    }

    if (isChunkLoadError({ error })) {
      if (!isChunkRetryInProgress()) {
        // Stale chunk after deployment: reload once with retry param to bust cache
        retryCurrentDocument();
        return;
      }
      // Retry already attempted and still failing — fall through to Sentry
    }

    Sentry.withScope((scope) => {
      scope.addBreadcrumb({
        data: {
          componentStack: errorInfo.componentStack,
          error,
        },
        level: "info",
      });

      Sentry.captureException(error);
    });
  }

  render() {
    const { error, hasError } = this.state;

    if (!hasError) {
      return this.props.children;
    }

    if (shouldIgnoreError({ error })) {
      return this.props.children;
    }

    return <ErrorSplash error={error} />;
  }
}

export default ErrorBoundary;
